<template>
  <div>
    <div class="d-flex justify-center">
      <table-pagination-controls
        :total-rows="reportResults.length"
        :current-page.sync="currentPage"
        :rows-per-page.sync="rowsPerPage"
        :per-page-options="pageOptions"
      />
    </div>
    <v-data-table
      dense
      hide-default-footer
      class="vf-table mt-2 mb-4"
      :items="reportResults"
      :headers="tableFields"
      :page="currentPage"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :items-per-page="rowsPerPage">
      <template v-slot:item.customer_name="row">
        {{ row.value }}
      </template>
      <template v-slot:item.total_route_count="row">
        {{ row.value }}
      </template>
      <template v-slot:item.temp_violation_count="row">
        {{ row.value }}
      </template>
      <template v-slot:item.temp_in_spec_percentage="row">
        {{ formatPercentage(row.value) }}
      </template>
      <template v-slot:item.freezer_violation_count="row">
        {{ row.value }}
      </template>
      <template v-slot:item.freezer_in_spec_percentage="row">
        {{ formatPercentage(row.value) }}
      </template>
      <template v-slot:item.cooler_violation_count="row">
        {{ row.value }}
      </template>
      <template v-slot:item.cooler_in_spec_percentage="row">
        {{ formatPercentage(row.value) }}
      </template>
      <template v-slot:foot>
        <tfoot>
          <tr class="white--text primary darken-1">
            <td>{{ $t('total') }}</td>
            <td>{{ overallTotalRouteCount }}</td>
            <td>{{ overallViolatingRouteCount }}</td>
            <td>{{ formatPercentage(percentInSpec(overallTotalRouteCount, overallViolatingRouteCount)) }}</td>
            <td>{{ overallFreezerViolationCount }}</td>
            <td>{{ formatPercentage(percentInSpec(overallTotalRouteCount, overallFreezerViolationCount)) }}</td>
            <td>{{ overallCoolerViolationCount }}</td>
            <td>{{ formatPercentage(percentInSpec(overallTotalRouteCount, overallCoolerViolationCount)) }}</td>
          </tr>
        </tfoot>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ReportRecord from '@/orm/models/ReportRecord'
import { get, sumBy, values } from 'lodash'

export default {
  name: 'MonthlyRouteStatusReport',
  components: {
    TablePaginationControls
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    report: {
      type: ReportRecord
    }
  },
  data () {
    return {
      currentPage: 1,
      rowsPerPage: 100,
      sortBy: 'customer_name',
      sortDesc: false,
      pageOptions: [10, 20, 50, 100, { value: -1, text: this.$t('all') }],
      tableFields: [
        {
          value: 'customer_name',
          text: this.$tc('distribution_center'),
          sortable: true
        },
        {
          value: 'total_route_count',
          text: this.$t('reports_ui.monthly_route_status_report.total_routes'),
          sortable: true
        },
        {
          value: 'temp_violation_count',
          text: this.$t('reports_ui.monthly_route_status_report.violating_routes'),
          sortable: true
        },
        {
          value: 'temp_in_spec_percentage',
          text: this.$t('reports_ui.monthly_route_status_report.overall_in_temp_routes'),
          sortable: true
        },
        {
          value: 'freezer_violation_count',
          text: this.$t('reports_ui.monthly_route_status_report.freezer_violating_routes'),
          sortable: true
        },
        {
          value: 'freezer_in_spec_percentage',
          text: this.$t('reports_ui.monthly_route_status_report.freezer_in_temp_routes'),
          sortable: true
        },
        {
          value: 'cooler_violation_count',
          text: this.$t('reports_ui.monthly_route_status_report.cooler_violating_routes'),
          sortable: true
        },
        {
          value: 'cooler_in_spec_percentage',
          text: this.$t('reports_ui.monthly_route_status_report.cooler_in_temp_routes'),
          sortable: true
        }
      ]
    }
  },
  computed: {
    reportResults () {
      return values(get(this, 'report.results'))
    },
    from () {
      return (this.rowsPerPage * (this.currentPage - 1)) + 1
    },
    to () {
      return this.rowsPerPage * this.currentPage
    },
    /**
     * Summation of total_route_count for use in the table footer.
     * @returns int
     */
    overallTotalRouteCount () {
      return sumBy(this.reportResults, x => {
        return Number(x.total_route_count)
      })
    },
    /**
     * Summation of temp_violation_count for use in the table footer.
     * @returns int
     */
    overallViolatingRouteCount () {
      return sumBy(this.reportResults, x => {
        return Number(x.temp_violation_count)
      })
    },
    /**
     * Summation of freezer_violation_count for use in the table footer.
     * @returns int
     */
    overallFreezerViolationCount () {
      return sumBy(this.reportResults, x => {
        return Number(x.freezer_violation_count)
      })
    },
    /**
     * Summation of cooler_violation_count for use in the table footer.
     * @returns int
     */
    overallCoolerViolationCount () {
      return sumBy(this.reportResults, x => {
        return Number(x.cooler_violation_count)
      })
    }
  },
  methods: {
    /**
     * Convenience method to format values as a percentage
     * @param value
     * @returns {string}
     */
    formatPercentage (value) {
      return (value * 100).toFixed(1) + '%'
    },

    /**
     * Calculate the percentage of routes in spec (ported from old PHP code)
     * @param routeCount
     * @param outOfSpecCount
     * @returns {number}
     */
    percentInSpec (routeCount, outOfSpecCount) {
      if (!routeCount) {
        // no out of spec routes means 100% in-spec
        return 1
      }
      return (routeCount - outOfSpecCount) / routeCount
    }
  }
}

</script>

<style scoped>

</style>
